import { useReactiveVar } from '@apollo/client';
import {
  ActionButton,
  Callout,
  CommandBarButton,
  DefaultButton,
  DirectionalHint,
  IconButton,
  PrimaryButton,
  Stack,
  Text,
  useTheme,
} from '@fluentui/react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getSmoothStepPath,
  Position,
  useReactFlow,
} from '@xyflow/react';
import {
  setFlowLayoutState,
  setNodeScrollState,
} from '../../../Conditions';
import { useId } from '@fluentui/react-hooks';
import { useState } from 'react';
import { useCommonStyles } from 'common/styles';
import { useFormContext } from 'react-hook-form';
import { ApprovalValues } from 'settings/account/intuitiveApprovalSetupFlow/ApprovalFlowDesigner/types';

export enum EdgeEnumValues {
  and = 'And',
  or = 'Or',
}
// @ts-ignore
export const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  ...rest
}) => {
  const { getValues, setValue } = useFormContext<ApprovalValues>();

  const flowLayoutState = useReactiveVar(setFlowLayoutState);
  const calloutId = useId(`customNode-And-${id}`);

  // const { setEdges } = useReactFlow();
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    targetPosition:
      flowLayoutState === 'horizontal' ? Position.Left : Position.Top,
    sourcePosition:
      flowLayoutState === 'horizontal' ? Position.Right : Position.Bottom,
  });
  const theme = useTheme();
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const isAnd = rest.label === EdgeEnumValues.and;
    const {  getEdges ,setEdges} = useReactFlow();
  
    const currentEdges = getEdges();

  //const currentEdges = useReactiveVar(edgesVar);

  const handleToggle = () => {
    const updatedEdges = currentEdges.map((edge) =>
      edge.id === id
        ? {
            ...edge,
            label:
              edge.label === EdgeEnumValues.and
                ? EdgeEnumValues.or
                : EdgeEnumValues.and,
          }
        : edge
    );

    const nodeIndex = parseInt(rest.target.split('-')[1]) - 2;
    const conditions = getValues('approvalSelectionConditions') || [];
    const updatedConditions = [...conditions];
    if (updatedConditions[nodeIndex]) {
      updatedConditions[nodeIndex].isAndCondition = !isAnd;
      setValue('approvalSelectionConditions', updatedConditions, {
        shouldDirty: true,
      });
    }

    setEdges(updatedEdges);
    setIsCalloutVisible(false);
    setNodeScrollState(true);
  };

  const commonStyles = useCommonStyles();

  return (
    <>
      <BaseEdge id={id} path={edgePath} type="step" />
      <EdgeLabelRenderer>
        {rest.label && (
          <Stack
            id={calloutId}
            style={{
              borderRadius: 5,
              padding: '5px 10px',
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              backgroundColor: isAnd
                ? theme.palette.tealLight
                : theme.palette.yellowDark,
              // border: `1px solid ${theme.palette.themePrimary}`,
            }}
            horizontal
            className="nodrag nopan"
            horizontalAlign="center"
            verticalAlign="center"
            tokens={{ childrenGap: 10 }}
            onClick={() => {
              setNodeScrollState(false);
              setIsCalloutVisible(true);
            }}
          >
            <Text
              style={{
                color: 'white',
              }}
            >
              {rest.label}
            </Text>

            <IconButton
              styles={{
                root: { width: 20, height: 20, borderRadius: 3 },
                icon: { color: 'white', fontSize: 10 },
                rootHovered: {
                  backgroundColor: isAnd
                    ? `${theme.palette.tealDark}33`
                    : `${theme.palette.yellowLight}77`,
                },
              }}
              iconProps={{ iconName: 'EditSolid12' }}
            />
          </Stack>
        )}

        {isCalloutVisible && (
          <Callout
            gapSpace={0}
            directionalHint={DirectionalHint.rightCenter}
            target={`#${calloutId}`}
            onDismiss={() => {
              setNodeScrollState(true);
              setIsCalloutVisible(false);
            }}
            setInitialFocus
          >
            <Stack tokens={{ childrenGap: 20, padding: 20 }}>
              <Text className={commonStyles.bold} variant="xLarge">
                Select Edge Label
              </Text>

              <Text variant="small">
                This determines the logic between conditions (e.g., AND vs OR)
              </Text>

              <Stack
                styles={{
                  root: {
                    alignSelf: 'end',
                    width: 'fit-content', // 👈 This makes the Stack shrink to fit its content
                    backgroundColor: !isAnd
                      ? theme.palette.tealLight
                      : theme.palette.yellowDark,
                  },
                }}
              >
                <DefaultButton
                  styles={{
                    root: {
                      color: 'white',
                      border: 'none',
                      backgroundColor: !isAnd
                        ? theme.palette.tealLight
                        : theme.palette.yellowDark,
                    },
                    rootHovered: {
                      color: 'white',
                      backgroundColor: !isAnd
                        ? `${theme.palette.tealDark}33`
                        : `${theme.palette.yellowLight}33`,
                    },
                    rootPressed: {
                      color: 'white',
                      backgroundColor: !isAnd
                        ? `${theme.palette.tealDark}33`
                        : `${theme.palette.yellowLight}33`,
                    },
                  }}
                  // styles={{ root: { width: 130 } }}
                  onClick={handleToggle}
                  text={`Change to '${
                    isAnd ? EdgeEnumValues.or : EdgeEnumValues.and
                  }'`}
                />
              </Stack>
            </Stack>
          </Callout>
        )}
      </EdgeLabelRenderer>
    </>
  );
};
